/**
 * MaintenanceGate Component - Phase 68 M5 Task 5.2
 *
 * Top-level gate that checks maintenance mode status and either:
 * - Renders MaintenanceScreen for non-super-admin users
 * - Renders children + MaintenanceBanner for super admins
 * - Renders children normally when not in maintenance
 *
 * Design reference: tasks.md § 5.2 MaintenanceGate Component
 */
import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Box, CircularProgress } from '@mui/material'

import { getMaintenanceStatus } from '@services/platformApi'
import MaintenanceScreen from './MaintenanceScreen'
import MaintenanceBanner from './MaintenanceBanner'

interface MaintenanceGateProps {
  children: React.ReactNode
}

/**
 * Maintenance mode gate.
 *
 * Fetches maintenance status every 60 seconds.
 * Decision logic:
 * - If loading (first fetch) → show children (fail-open for UX)
 * - If error → show children (fail-open for resilience)
 * - If active && !is_super_admin → show MaintenanceScreen
 * - If active && is_super_admin → show MaintenanceBanner + children
 * - If not active → show children normally
 */
const MaintenanceGate: React.FC<MaintenanceGateProps> = ({ children }) => {
  const { data: status, isLoading, isError } = useQuery({
    queryKey: ['platform', 'maintenance-status'],
    queryFn: getMaintenanceStatus,
    refetchInterval: 60 * 1000, // 60 seconds
    staleTime: 30 * 1000, // 30 seconds
    // Don't show loading spinner on refetch
    refetchOnWindowFocus: false,
    // Fail open — if we can't reach the API, let users through
    retry: 1,
  })

  // Fail-open on loading or error — don't block users
  if (isLoading || isError) {
    return <>{children}</>
  }

  // Maintenance mode active
  if (status?.active) {
    // Super admin can still access
    if (status.is_super_admin) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <MaintenanceBanner />
          <Box sx={{ flex: 1 }}>
            {children}
          </Box>
        </Box>
      )
    }

    // Non-super-admin users see maintenance screen
    return <MaintenanceScreen message={status.message} />
  }

  // Not in maintenance mode — render normally
  return <>{children}</>
}

export default MaintenanceGate
